/**
 * The bank texts kept unnamed (TMMAPP-0164 item 12; TMMAPI-0195 U3): `GET /v1/me/payees/unnamed
 * ?kept=true`, 50 at a time with Show more. Each row is the text, its transactions and last date, with
 * **Restore** (`…/unnamed/restore`), which puts it back on the list to name: the row leaves the dialog
 * and the host re-reads. A viewer who can't write sees the list without Restore. 600 wide, full-screen
 * below `sm`, as Forgotten suggestions.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { errorCopy } from '@/api/errorCopy';
import { ErrorCodes, isApiError } from '@/api/errors';
import { unnamedPayeesApi, type UnnamedPayeeGroup } from '@/api/unnamedPayees';
import { ErrorState } from '@/components/ErrorState';
import { SubmitButton } from '@/components/SubmitButton';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useOpenSubscribe } from '@/features/subscribe/subscribeOfferContext';
import { SubscriberActionHint, useSubscriberAction } from '@/session/useSubscriberAction';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import type { NotifyOptions } from '@/shell/NotifyProvider';
import { resolveTokens } from '@/theme/theme';

interface KeptUnnamedDialogProps {
  readOnly: boolean;
  /** A text is back on the list: the host refreshes its counts and its list. */
  onRestored: () => void;
  onNotify?: ((message: string, options?: NotifyOptions) => void) | undefined;
  onClose: () => void;
}

export function KeptUnnamedDialog({
  readOnly,
  onRestored,
  onNotify,
  onClose,
}: KeptUnnamedDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const fullScreen = useDialogFullScreen();
  const titleId = useId();
  const [groups, setGroups] = useState<UnnamedPayeeGroup[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [restoring, setRestoring] = useState<string | null>(null);
  const openSubscribe = useOpenSubscribe();

  const loadFirst = useCallback(async () => {
    setFailure(null);
    try {
      const page = await unnamedPayeesApi.list({ kept: true });
      setGroups(page.groups);
      setCursor(page.nextCursor);
    } catch (error) {
      setFailure({ error });
    }
  }, []);

  // StrictMode double-invokes the effect; the first page is read once.
  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    void loadFirst();
  }, [loadFirst]);

  async function loadMore() {
    if (cursor === null || loadingMore || groups === null) return;
    setLoadingMore(true);
    setFailure(null);
    try {
      const page = await unnamedPayeesApi.list({ kept: true, cursor });
      const seen = new Set(groups.map((group) => group.key));
      setGroups([...groups, ...page.groups.filter((group) => !seen.has(group.key))]);
      setCursor(page.nextCursor);
    } catch (error) {
      setFailure({ error });
    } finally {
      setLoadingMore(false);
    }
  }

  async function restore(key: string) {
    if (restoring !== null) return;
    setRestoring(key);
    try {
      await unnamedPayeesApi.restore(key);
      setGroups((prev) => (prev === null ? prev : prev.filter((group) => group.key !== key)));
      onNotify?.(t('naming.unnamed.restored'));
      onRestored();
    } catch (error) {
      if (isApiError(error) && error.code === ErrorCodes.PAYEE_TEXT_NOT_FOUND) {
        // Named or gone meanwhile: the row leaves quietly.
        setGroups((prev) => (prev === null ? prev : prev.filter((group) => group.key !== key)));
        onRestored();
      } else {
        onNotify?.(errorCopy(t, error, 'naming.unnamed.failed'), { severity: 'error' });
      }
    } finally {
      setRestoring(null);
    }
  }

  // Restore is gated like the section's other actions (TMMAPP-0166 item 3): a GUEST or NONE tier's
  // click closes this dialog, then the purchase opens (DLG-005).
  const restoreAction = useSubscriberAction(
    (event) => {
      const key = event.currentTarget.dataset.keptKey;
      if (key !== undefined) void restore(key);
    },
    false,
    () => {
      onClose();
      openSubscribe();
    },
  );

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="sm"
      aria-labelledby={titleId}
      sx={{ '& .MuiDialog-paper': { maxWidth: fullScreen ? undefined : 600 } }}
      data-kept-unnamed=""
    >
      <DialogTitle id={titleId}>{t('naming.unnamed.keptTitle')}</DialogTitle>
      <DialogContent>
        <SubscriberActionHint
          id={restoreAction.descriptionId}
          description={restoreAction.description}
        />
        {failure !== null && (
          <ErrorState
            variant="section"
            error={failure.error}
            what={t('errors.what.unnamedPayees')}
            onRetry={() => void (groups === null ? loadFirst() : loadMore())}
          />
        )}
        {groups === null && failure === null && (
          <Skeleton variant="rounded" height={120} aria-busy="true" />
        )}
        {groups !== null && groups.length === 0 && cursor === null && (
          <Typography variant="body2" sx={{ color: tokens.text2 }}>
            {t('naming.unnamed.keptEmpty')}
          </Typography>
        )}
        {groups !== null && groups.length > 0 && (
          <Box
            component="ul"
            aria-label={t('naming.unnamed.keptTitle')}
            sx={{ listStyle: 'none', m: 0, p: 0 }}
          >
            {groups.map((group) => (
              <Box
                component="li"
                key={group.key}
                data-kept-row={group.key}
                sx={{
                  py: 1.5,
                  borderTop: `1px solid ${tokens.line}`,
                  '&:first-of-type': { borderTop: 0 },
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  sx={{ gap: 1, alignItems: { sm: 'center' } }}
                >
                  <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="mono" component="span" sx={{ overflowWrap: 'anywhere' }}>
                      {group.bankText}
                    </Typography>
                    <Typography variant="body2" sx={{ color: tokens.text2 }}>
                      {t('naming.unnamed.linesLast', {
                        count: group.lineCount,
                        date: formatCivilDate(group.lastOn, locale),
                      })}
                    </Typography>
                  </Stack>
                  {!readOnly && (
                    <SubmitButton
                      size="small"
                      variant="outlined"
                      loading={restoring === group.key}
                      disabled={restoring !== null && restoring !== group.key}
                      aria-label={t('naming.unnamed.restoreNamed', { bank: group.bankText })}
                      aria-describedby={restoreAction.descriptionId}
                      data-kept-key={group.key}
                      onClick={restoreAction.run}
                      sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'center' } }}
                    >
                      {t('naming.unnamed.restore')}
                    </SubmitButton>
                  )}
                </Stack>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {cursor !== null && groups !== null && (
          <SubmitButton variant="outlined" loading={loadingMore} onClick={() => void loadMore()}>
            {t('naming.unnamed.showMore')}
          </SubmitButton>
        )}
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
